import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  User,
  Company,
  LoginRequest,
  RegisterRequest,
  UserRole,
} from "../types";
import apiService from "../services/api";
import { getErrorMessage } from "../utils/errorMessages";

interface AuthContextType {
  user: User | null;
  company: Company | null;
  loading: boolean;
  error: string | null;
  pendingVerification: boolean;
  verificationEmail: string;
  login: (credentials: LoginRequest) => Promise<void>;
  loginJobSeeker: (
    credentials: LoginRequest,
  ) => Promise<{ requiresPasswordSetup: boolean; email?: string }>;
  register: (userData: RegisterRequest) => Promise<void>;
  registerJobSeeker: (userData: {
    email: string;
    first_name: string;
    last_name: string;
    country_code?: string;
    phone?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }) => Promise<void>;
  registerCompanyUser: (userData: {
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    country_code?: string;
    phone?: string;
    job_title?: string;
    company_website?: string;
    company_industry?: string;
    company_size?: string;
    subscription_tier?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  canManageUsers: boolean;
  canManageCompanySettings: boolean;
  canManageJobs: boolean;
  canManageCandidates: boolean;
  canUploadResumes: boolean;
  canViewAnalytics: boolean;
  hasProPlanAccess: () => boolean;
  refreshCompanyData: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUserState: (userData: Partial<User>) => void;
  setPendingVerification: (pending: boolean) => void;
  setVerificationEmail: (email: string) => void;
  loginCompanyUser: (
    credentials: LoginRequest,
  ) => Promise<{ requiresPasswordSetup: boolean; email?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  const patchUserCompanyId = (rawUser: any) => {
    if (!rawUser) {
      return rawUser;
    }

    const user = { ...rawUser };

    if (!user.company_id && user.company?.id) {
      user.company_id = user.company.id;
    }

    // Ensure persona is always present for UI gating logic after refresh/hydration.
    if (!user.persona) {
      if (user.is_super_admin) {
        user.persona = "super_admin";
      } else if (user.company_id || user.company?.id) {
        user.persona = "company_user";
      } else {
        user.persona = "job_seeker";
      }
    }

    return user;
  };

  const initializeAuth = useCallback(async () => {
    try {
      setError(null);
      console.log("🔄 Initializing authentication...");

      // Check if we're on a public route that doesn't need authentication
      const currentPath = window.location.pathname;
      const isPublicRoute =
        currentPath.startsWith("/profile/") ||
        currentPath === "/" ||
        currentPath.startsWith("/auth/");

      const token = localStorage.getItem("access_token");
      const storedUser = localStorage.getItem("user");
      const storedCompany = localStorage.getItem("company");

      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log("📦 Found stored user:", parsedUser.email);

          // Set user immediately from storage
          const patchedUser = patchUserCompanyId(parsedUser);
          setUser(patchedUser);

          // Set company from storage if available
          if (storedCompany) {
            try {
              const parsedCompany = JSON.parse(storedCompany);
              setCompany(parsedCompany);
              console.log("📦 Found stored company:", parsedCompany.name);
            } catch (e) {
              console.warn("Failed to parse stored company data:", e);
            }
          }

          // Only try to refresh user data if NOT on a public route
          if (!isPublicRoute) {
            try {
              console.log("🔄 Refreshing user data...");
              const refreshedUser = await apiService.getCurrentUser();
              const refreshedPatchedUser = patchUserCompanyId(refreshedUser);
              setUser(refreshedPatchedUser);
              localStorage.setItem(
                "user",
                JSON.stringify(refreshedPatchedUser),
              );
              console.log("✅ User data refreshed successfully");

              // Try to refresh company data if user has company_id
              const refreshedCompanyId =
                refreshedPatchedUser.company_id ||
                refreshedPatchedUser.company?.id;
              if (refreshedCompanyId && !refreshedPatchedUser.is_super_admin) {
                console.log("🏢 Extracting company data from user object...");
                // Extract company data directly from user object instead of making API call
                if (refreshedPatchedUser.company) {
                  console.log(
                    "🏢 Setting company data from refreshed user:",
                    refreshedPatchedUser.company.name,
                  );
                  setCompany(refreshedPatchedUser.company);
                  localStorage.setItem(
                    "company",
                    JSON.stringify(refreshedPatchedUser.company),
                  );
                  console.log("✅ Company data extracted successfully");
                } else {
                  console.warn(
                    "⚠️ No company data found in refreshed user object",
                  );
                  // Fallback to stored company data
                  const storedCompany = localStorage.getItem("company");
                  if (storedCompany) {
                    try {
                      const parsedCompany = JSON.parse(storedCompany);
                      setCompany(parsedCompany);
                      console.log(
                        "📦 Using stored company data as fallback:",
                        parsedCompany.name,
                      );
                    } catch (e) {
                      console.error("Failed to parse stored company data:", e);
                    }
                  }
                }
              }
            } catch (refreshError: any) {
              console.warn(
                "⚠️ User data refresh failed (non-blocking):",
                refreshError,
              );
              // Don't immediately clear auth data - let the API service handle token refresh
              // Only clear if we get a specific authentication error that can't be recovered
              if (
                refreshError.message?.includes(
                  "Could not validate credentials",
                ) &&
                !localStorage.getItem("refresh_token")
              ) {
                console.log(
                  "🧹 No refresh token available and credentials invalid, clearing auth data",
                );
                apiService.clearAllAuthData();
                setUser(null);
                setCompany(null);
              } else {
                console.log(
                  "⏳ Token may be expired, but refresh token available - API service will handle refresh",
                );
              }
            }
          } else {
            console.log(
              "📍 On public route, skipping user data refresh to avoid unnecessary API calls",
            );
          }
        } catch (parseError) {
          console.error("❌ Failed to parse stored user data:", parseError);
          // Clear corrupted data
          apiService.clearAllAuthData();
        }
      } else {
        console.log("📭 No stored authentication data found");
      }
    } catch (error) {
      console.error("❌ Auth initialization failed:", error);
      setError("Failed to initialize authentication");
      // Don't clear auth data on initialization failure - might be network issue
    } finally {
      setLoading(false);
      console.log("✅ Auth initialization complete");
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const loadCompanyData = async (companyId: string) => {
    try {
      setError(null);
      // Check if we have a valid access token before making the request
      if (!apiService.isAuthenticated()) {
        console.warn("No access token available, skipping company data load");
        return;
      }

      const companyData = await apiService.getCompany(companyId);
      setCompany(companyData);
      localStorage.setItem("company", JSON.stringify(companyData));
    } catch (error: any) {
      console.warn("Failed to load company data:", error);

      // If it's an authentication error, don't set error state to avoid UI issues
      if (
        error.message?.includes("401") ||
        error.message?.includes("Could not validate credentials")
      ) {
        console.warn(
          "Authentication error loading company data, will retry after login",
        );
        return;
      }

      setError("Failed to load company data");
      // Try to get from localStorage as fallback
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        try {
          const parsedCompany = JSON.parse(storedCompany);
          setCompany(parsedCompany);
        } catch (e) {
          console.error("Failed to parse stored company data:", e);
        }
      }
    }
  };

  const refreshCompanyData = async () => {
    if (user?.company_id && !user.is_super_admin) {
      await loadCompanyData(user.company_id);
    }
  };

  const login = async (credentials: LoginRequest) => {
    try {
      console.log(
        "🔐 AuthContext: Login function called for:",
        credentials.email,
      );

      setError(null);
      setLoading(true);
      console.log("🔍 AuthContext: About to call apiService.login");

      const response = await apiService.login(credentials);
      console.log("🔍 AuthContext: apiService.login returned:", response);

      if (response.user) {
        const patchedUser = patchUserCompanyId(response.user);
        patchedUser.persona = response.persona || patchedUser.persona;
        console.log("✅ Login successful, user:", patchedUser);
        console.log("🔑 User persona:", response.persona);
        console.log("🏢 User company_id:", patchedUser.company_id);
        console.log("🔧 User is_super_admin:", patchedUser.is_super_admin);

        setUser(patchedUser);
        localStorage.setItem("user", JSON.stringify(patchedUser));

        console.log(
          "🔍 AuthContext: User state set, checking authentication...",
        );
        console.log("🔍 AuthContext: isAuthenticated will be:", !!patchedUser);

        // Extract company data directly from user object if available
        if (patchedUser.company) {
          console.log(
            "🏢 Setting company data from login response:",
            patchedUser.company.name,
          );
          setCompany(patchedUser.company);
          localStorage.setItem("company", JSON.stringify(patchedUser.company));
        } else {
          // Legacy fallback - only call loadCompanyData if no company data in user object
          const companyId = patchedUser.company_id;
          if (companyId && !patchedUser.is_super_admin) {
            console.log(
              "🏢 No company data in user object, attempting to load separately:",
              companyId,
            );
            try {
              await loadCompanyData(companyId);
            } catch (companyError: any) {
              console.warn(
                "⚠️ Company data loading failed (non-blocking):",
                companyError,
              );
            }
          }
        }

        console.log("✅ AuthContext: Login process completed successfully");
      } else {
        throw new Error("No user data received");
      }
    } catch (error: any) {
      console.error("❌ AuthContext: Login error:", error);
      setError(getErrorMessage(error, "Login failed"));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginJobSeeker = async (credentials: LoginRequest) => {
    try {
      console.log(
        "🔐 AuthContext: LoginJobSeeker function called for:",
        credentials.email,
      );

      setError(null);
      setLoading(true);
      console.log("🔍 AuthContext: About to call apiService.loginJobSeeker");

      const response = await apiService.loginJobSeeker(credentials);
      console.log(
        "🔍 AuthContext: apiService.loginJobSeeker returned:",
        response,
      );

      if (response.user) {
        const patchedUser = patchUserCompanyId(response.user);
        console.log("✅ LoginJobSeeker successful, user:", patchedUser);
        console.log("🔑 User persona:", response.persona);
        console.log(
          "🔒 Requires password setup:",
          patchedUser.requires_password_setup,
        );

        // Store persona in user object
        patchedUser.persona = response.persona;

        setUser(patchedUser);
        localStorage.setItem("user", JSON.stringify(patchedUser));

        // Extract company data directly from user object if available
        if (patchedUser.company) {
          console.log(
            "🏢 Setting company data from job seeker login response:",
            patchedUser.company.name,
          );
          setCompany(patchedUser.company);
          localStorage.setItem("company", JSON.stringify(patchedUser.company));
        } else {
          // Legacy fallback - only call loadCompanyData if no company data in user object
          const companyId = patchedUser.company_id;
          if (companyId && !patchedUser.is_super_admin) {
            console.log(
              "🏢 No company data in user object, attempting to load separately:",
              companyId,
            );
            try {
              await loadCompanyData(companyId);
            } catch (companyError: any) {
              console.warn(
                "⚠️ Company data loading failed (non-blocking):",
                companyError,
              );
            }
          }
        }

        // Check if user needs to set up permanent password
        if (patchedUser.requires_password_setup) {
          console.log("🔒 User needs password setup, redirecting...");
          // Don't navigate here, let the LoginPage handle it
          return { requiresPasswordSetup: true, email: credentials.email };
        }

        console.log(
          "✅ AuthContext: LoginJobSeeker process completed successfully",
        );
        return { requiresPasswordSetup: false };
      } else {
        throw new Error("No user data received");
      }
    } catch (error: any) {
      console.error("❌ AuthContext: LoginJobSeeker error:", error);
      setError(getErrorMessage(error, "Login failed"));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      setError(null);
      setLoading(true);
      const response = await apiService.register(userData);
      if (response.user) {
        const patchedUser = patchUserCompanyId(response.user);
        setUser(patchedUser);
        localStorage.setItem("user", JSON.stringify(patchedUser));
        const companyId = patchedUser.company_id || patchedUser.company?.id;
        if (companyId && !patchedUser.is_super_admin) {
          await loadCompanyData(companyId);
        }
      } else {
        throw new Error("Failed to get user data after registration");
      }
    } catch (error: any) {
      setError(error.message || "Registration failed");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const registerJobSeeker = async (userData: {
    email: string;
    first_name: string;
    last_name: string;
    country_code?: string;
    phone?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }) => {
    try {
      setError(null);
      setLoading(true);
      console.log(
        "🚀 Starting job seeker registration with temp password flow...",
      );

      const response = await apiService.registerJobSeeker(userData);
      console.log("📦 Registration response received:", response);

      // Set pending verification state
      setPendingVerification(true);
      setVerificationEmail(userData.email);
      console.log("✅ Pending verification state set for job seeker");

      console.log("🏁 Job seeker registration completed successfully");
    } catch (error: any) {
      console.error("❌ Job seeker registration failed:", error);
      setError(error.message || "Registration failed");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const registerCompanyUser = async (userData: {
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    country_code?: string;
    phone?: string;
    job_title?: string;
    company_website?: string;
    company_industry?: string;
    company_size?: string;
    subscription_tier?: string;
    location?: string;
    country?: string;
    state?: string;
    city?: string;
  }) => {
    try {
      setError(null);
      setLoading(true);
      console.log(
        "🚀 Starting company user registration with temp password flow...",
      );

      const response = await apiService.registerCompanyUser(userData);
      console.log("📦 Registration response received:", response);

      // Set pending verification state
      setPendingVerification(true);
      setVerificationEmail(userData.email);
      console.log("✅ Pending verification state set for company user");

      console.log("🏁 Company user registration completed successfully");
    } catch (error: any) {
      console.error("❌ Company user registration failed:", error);
      setError(error.message || "Registration failed");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log("🚪 Starting comprehensive logout process...");

    // Call API service logout (which now does aggressive cleanup)
    apiService.logout();

    // Clear React state immediately
    setUser(null);
    setCompany(null);
    setError(null);
    setPendingVerification(false);
    setVerificationEmail("");

    // Additional aggressive cleanup
    console.log("🧹 AuthContext: Additional storage cleanup...");

    // Clear ALL localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();

    // Clear cookies
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;

      if (window.location.hostname === "localhost") {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=localhost`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.localhost`;
      }
    });

    // Clear Service Worker caches
    if ("caches" in window) {
      caches
        .keys()
        .then((cacheNames) => {
          return Promise.all(
            cacheNames.map((cacheName) => caches.delete(cacheName)),
          );
        })
        .then(() => {
          console.log("✅ AuthContext: All caches cleared");
        })
        .catch((error) => {
          console.log("⚠️ AuthContext: Error clearing caches:", error);
        });
    }

    console.log("✅ AuthContext: Comprehensive logout completed");

    // Force a small delay then verify cleanup
    setTimeout(() => {
      const remainingLocalStorage = Object.keys(localStorage).length;
      const remainingSessionStorage = Object.keys(sessionStorage).length;

      console.log("📊 AuthContext cleanup verification:", {
        localStorageItems: remainingLocalStorage,
        sessionStorageItems: remainingSessionStorage,
        authToken: localStorage.getItem("access_token"),
        user: localStorage.getItem("user"),
        company: localStorage.getItem("company"),
      });

      if (remainingLocalStorage === 0 && remainingSessionStorage === 0) {
        console.log("✅ Perfect! All storage completely cleared!");
      } else {
        console.warn(
          "⚠️ Some items still remain - forcing page reload to ensure clean state",
        );
        // Force page reload if storage isn't completely clean
        window.location.href = "/auth/login";
      }
    }, 100);
  };

  const updateUserState = (userData: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...userData };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const refreshUser = async () => {
    try {
      setError(null);
      if (!user) return;
      const currentUser = await apiService.getCurrentUser();
      const patchedUser = patchUserCompanyId(currentUser);
      setUser(patchedUser);
      localStorage.setItem("user", JSON.stringify(patchedUser));
      const companyId = patchedUser.company_id || patchedUser.company?.id;
      if (companyId && !patchedUser.is_super_admin) {
        await loadCompanyData(companyId);
      }
    } catch (error: any) {
      setError(error.message || "Failed to refresh user data");
      throw error;
    }
  };

  // Permission helpers
  const isAuthenticated = !!user;
  const isSuperAdmin = user?.is_super_admin || false;
  const isCompanyAdmin = user?.role === UserRole.ADMIN || isSuperAdmin;

  // Check if company has PRO plan access for advanced features like job management
  const hasProPlanAccess = () => {
    if (isSuperAdmin) return true; // Super admins have access to everything
    if (company) return true;

    // Check pricing plan only (we moved away from subscription tiers)
  /*if (company.pricing_plan) {
      const planName = company.pricing_plan.name.toLowerCase();
      return (
        planName.includes("pro") ||
        planName.includes("professional") ||
        planName.includes("premium") ||
        planName.includes("enterprise")
      );
    }
*/
    // If no pricing plan is set, default to false (no PRO access)
    return false;
  };

  // Updated role permissions:
  // - Admin: Full access (unchanged)
  // - Recruiter: Everything except User Management & Company Settings
  // - Viewer: Read-only access
  const canManageUsers = isCompanyAdmin; // Only Admins can manage users
  const canManageCompanySettings = isCompanyAdmin; // Only Admins can manage company settings
  const canManageJobs =
    (isCompanyAdmin || user?.role === UserRole.RECRUITER) && hasProPlanAccess(); // Admins and Recruiters with PRO plan
  const canManageCandidates =
    isCompanyAdmin || user?.role === UserRole.RECRUITER; // Admins and Recruiters
  const canUploadResumes = canManageCandidates && !user?.is_super_admin; // Admins and Recruiters (not super admins)
  const canViewAnalytics = isAuthenticated;

  const loginCompanyUser = async (credentials: LoginRequest) => {
    try {
      console.log(
        "🔐 AuthContext: LoginCompanyUser function called for:",
        credentials.email,
      );

      setError(null);
      setLoading(true);
      console.log("🔍 AuthContext: About to call apiService.loginCompanyUser");

      const response = await apiService.loginCompanyUser(credentials);
      console.log(
        "🔍 AuthContext: apiService.loginCompanyUser returned:",
        response,
      );

      if (response.user) {
        const patchedUser = patchUserCompanyId(response.user);
        console.log("✅ LoginCompanyUser successful, user:", patchedUser);
        console.log("🔑 User persona:", response.persona);
        console.log(
          "🔒 Requires password setup:",
          patchedUser.requires_password_setup,
        );

        // Store persona in user object
        patchedUser.persona = response.persona;

        setUser(patchedUser);
        localStorage.setItem("user", JSON.stringify(patchedUser));

        // Extract company data directly from user object if available
        if (patchedUser.company) {
          console.log(
            "🏢 Setting company data from user object:",
            patchedUser.company.name,
          );
          setCompany(patchedUser.company);
          localStorage.setItem("company", JSON.stringify(patchedUser.company));
        } else {
          // Legacy fallback - only call loadCompanyData if no company data in user object
          const companyId = patchedUser.company_id;
          if (companyId && !patchedUser.is_super_admin) {
            console.log(
              "🏢 No company data in user object, attempting to load separately:",
              companyId,
            );
            try {
              await loadCompanyData(companyId);
            } catch (companyError: any) {
              console.warn(
                "⚠️ Company data loading failed (non-blocking):",
                companyError,
              );
            }
          }
        }

        // Check if user needs to set up permanent password
        if (patchedUser.requires_password_setup) {
          console.log("🔒 Company user needs password setup, redirecting...");
          // Don't navigate here, let the LoginPage handle it
          return { requiresPasswordSetup: true, email: credentials.email };
        }

        console.log(
          "✅ AuthContext: LoginCompanyUser process completed successfully",
        );
        return { requiresPasswordSetup: false };
      } else {
        throw new Error("No user data received");
      }
    } catch (error: any) {
      console.error("❌ AuthContext: LoginCompanyUser error:", error);
      setError(getErrorMessage(error, "Login failed"));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    company,
    loading,
    error,
    pendingVerification,
    verificationEmail,
    login,
    loginJobSeeker,
    register,
    logout,
    isAuthenticated,
    isSuperAdmin,
    isCompanyAdmin,
    canManageUsers,
    canManageCompanySettings,
    canManageJobs,
    canManageCandidates,
    canUploadResumes,
    canViewAnalytics,
    refreshCompanyData,
    refreshUser,
    updateUserState,
    registerCompanyUser,
    registerJobSeeker,
    setPendingVerification,
    setVerificationEmail,
    loginCompanyUser,
    hasProPlanAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
